import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { useGetAvailableFeaturesQuery } from 'modules/organizer/store/available-features-api';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

interface useFetchAvailableFeatureProps {
  userTournamentId?: number;
  isEnabled?: boolean;
}

/**
 * Фичи тарифа текущей организации (RTK Query, тег `AvailableFeature` — сбрасывается после оплаты).
 *
 * Форма ответа — прежняя, react-query-шная: `data`, `isFetching`, `isLoading`, `error`, `refetch`.
 * Два отличия от сырого RTK-хука сделаны намеренно:
 * - `isFetching` / `isLoading` — «данных для этой организации ещё нет». Фоновый перезапрос после
 *   оплаты их не поднимает: `useFeature` → `feature-route.tsx`, вкладка «Медиа» на время
 *   `isFetching` рисуют пустоту, и экран мигал бы.
 * - `refetch` безопасен при `skip`: RTK бросает на `refetch` незапущенного запроса, а
 *   «Повторить» чеклиста оверлея зовёт его без оглядки.
 */
export function useFetchAvailableFeature(props?: useFetchAvailableFeatureProps) {
  const { userTournamentId: extraUserTournamentId, isEnabled = true } = props ?? {};

  const userTournamentId = useSelector(authStoreUserTournamentId) || extraUserTournamentId;
  const skip = !userTournamentId || !isEnabled;

  const query = useGetAvailableFeaturesQuery(userTournamentId as number, { skip });
  // `currentData`, а не `data`: при смене организации `data` ещё держит фичи прежней.
  const data = query.currentData;
  const isPending = !skip && data === undefined && query.isFetching;

  const refetchRef = useRef(query.refetch);
  useEffect(() => {
    refetchRef.current = query.refetch;
  });

  const refetch = useCallback(() => {
    if (!skip) refetchRef.current();
  }, [skip]);

  return {
    data,
    isFetching: isPending,
    isLoading: isPending,
    error: query.isFetching ? undefined : query.error,
    refetch,
  };
}
